import React, { useState, useEffect, useCallback } from 'react';
import { PreKPracticeSettings } from '../types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../../hooks/usePreKAudio';
import {
  PracticeHeader,
  PracticeAnswerGrid,
  PRACTICE_PHASES,
  PRACTICE_TIMER_CONSTANTS,
  generatePracticeAnswerChoices,
  PREK_PRACTICE_TRACKS,
} from '../shared';

interface WeDoScreenProps {
  settings: PreKPracticeSettings;
  factsToPractice: { factId: string; digit: number }[];
  onComplete: () => void;
  onBack: () => void;
}

const WeDoScreen: React.FC<WeDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedQueue, setFailedQueue] = useState<{ factId: string; digit: number }[]>([]);
  const [isRetryPhase, setIsRetryPhase] = useState(false);
  const [currentRetryIndex, setCurrentRetryIndex] = useState(0);

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [highlightCorrect, setHighlightCorrect] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());

  const { playTTSWithCallback, playChime, isTTSPlaying } = usePreKTTS();

  // Get current fact
  const getCurrentFact = useCallback(() => {
    if (isRetryPhase) {
      return failedQueue[currentRetryIndex];
    }
    return factsToPractice[currentFactIndex];
  }, [isRetryPhase, failedQueue, currentRetryIndex, factsToPractice, currentFactIndex]);

  const currentFact = getCurrentFact();
  const currentDigit = currentFact?.digit;

  // Initialize answer choices when digit changes
  useEffect(() => {
    if (currentDigit !== undefined) {
      setAnswerChoices(generatePracticeAnswerChoices(PREK_PRACTICE_TRACKS.NAMING, currentDigit));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setHighlightCorrect(false);
      setCorrectAnswerGiven(false);
    }
  }, [currentDigit]);

  // Play TTS instruction when component loads
  useEffect(() => {
    if (!ttsHasPlayed && settings.speechEnabled && currentDigit !== undefined) {
      const audioFile =
        QUESTION_SCREEN_AUDIO.tapNumber[currentDigit as keyof typeof QUESTION_SCREEN_AUDIO.tapNumber];

      playTTSWithCallback(audioFile, () => {
        setTtsHasPlayed(true);
        // Show correct answer highlight briefly after TTS
        setTimeout(() => {
          setHighlightCorrect(true);
          setTimeout(() => setHighlightCorrect(false), PRACTICE_TIMER_CONSTANTS.WE_DO_HIGHLIGHT_MS);
        }, 500);
      }).catch(() => {
        setTtsHasPlayed(true);
        // Still show highlight even if TTS fails
        setTimeout(() => {
          setHighlightCorrect(true);
          setTimeout(() => setHighlightCorrect(false), PRACTICE_TIMER_CONSTANTS.WE_DO_HIGHLIGHT_MS);
        }, 500);
      });
    } else if (!settings.speechEnabled) {
      // If speech disabled, still show the highlight
      setTtsHasPlayed(true);
      setTimeout(() => {
        setHighlightCorrect(true);
        setTimeout(() => setHighlightCorrect(false), PRACTICE_TIMER_CONSTANTS.WE_DO_HIGHLIGHT_MS);
      }, 1000);
    }
  }, [currentDigit, settings.speechEnabled, playTTSWithCallback, ttsHasPlayed]);

  const advanceToNext = useCallback(() => {
    if (isRetryPhase) {
      // In retry phase - after removing an item, check if queue is empty
      if (failedQueue.length === 0) {
        // Queue is empty, we're done!
        onComplete();
      } else if (currentRetryIndex >= failedQueue.length) {
        // Current index is beyond queue length, reset to 0
        setCurrentRetryIndex(0);
      } else if (currentRetryIndex < failedQueue.length - 1) {
        // Move to next item in retry queue
        setCurrentRetryIndex(currentRetryIndex + 1);
      } else {
        // At end of retry queue, restart from beginning
        setCurrentRetryIndex(0);
      }
    } else {
      // In original phase
      if (currentFactIndex < factsToPractice.length - 1) {
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        // Finished original sequence
        if (failedQueue.length > 0) {
          // Start retry phase
          setIsRetryPhase(true);
          setCurrentRetryIndex(0);
        } else {
          // All done!
          onComplete();
        }
      }
    }

    // Reset answer state for new question
    setHasAnswered(false);
    setCorrectAnswerGiven(false);
    setTtsHasPlayed(false);
  }, [
    isRetryPhase,
    currentRetryIndex,
    failedQueue.length,
    currentFactIndex,
    factsToPractice.length,
    onComplete,
  ]);

  const handleAnswerClick = useCallback(
    (selectedDigit: number) => {
      // Don't allow clicks while TTS is playing or already answered
      if (isTTSPlaying || hasAnswered) return;

      setHasAnswered(true);
      const isCorrect = selectedDigit === currentDigit;

      if (isCorrect) {
        setCorrectAnswerGiven(true);

        // Update fact score: +1 for correct
        setFactScores((prev) => {
          const newScores = new Map(prev);
          const currentScore = newScores.get(currentFact.factId) || 0;
          const newScore = currentScore + 1;
          newScores.set(currentFact.factId, newScore);

          // Only increment counter if going from 0 to 1 (first genuine correct)
          if (currentScore === 0 && newScore === 1) {
            setCompletedCount((prevCount) => prevCount + 1);
          }

          return newScores;
        });

        if (isRetryPhase) {
          // In retry phase - remove from failed queue
          setFailedQueue((prev) => {
            const newQueue = prev.filter((_, index) => index !== currentRetryIndex);
            // Check if we need to complete after this removal
            if (newQueue.length === 0) {
              setTimeout(() => onComplete(), 600);
            } else {
              // Adjust currentRetryIndex after removal
              setTimeout(() => {
                if (currentRetryIndex >= newQueue.length) {
                  setCurrentRetryIndex(0);
                }
              }, 600);
            }
            return newQueue;
          });
        }

        // Play chime and advance after a delay (but not in retry phase where we handle advancement in setFailedQueue)
        playChime().then(() => {
          if (!isRetryPhase) {
            setTimeout(() => {
              advanceToNext();
            }, 500);
          }
        });
      } else {
        // Incorrect answer

        // Update fact score: -1 for incorrect
        setFactScores((prev) => {
          const newScores = new Map(prev);
          const currentScore = newScores.get(currentFact.factId) || 0;
          const newScore = currentScore - 1;
          newScores.set(currentFact.factId, newScore);

          // If score goes below 1, decrement counter (was previously counted but now incorrect)
          if (currentScore >= 1 && newScore < 1) {
            setCompletedCount((prevCount) => prevCount - 1);
          }

          return newScores;
        });

        if (!correctAnswerGiven) {
          // First attempt was wrong - add to failed queue (only if not already there)
          if (!isRetryPhase) {
            setFailedQueue((prev) => {
              const alreadyExists = prev.some((f) => f.factId === currentFact.factId);
              if (!alreadyExists) {
                return [...prev, currentFact];
              }
              return prev;
            });
          } else {
            // In retry phase and still wrong - add back to end of failed queue
            const failedFact = failedQueue[currentRetryIndex];
            setFailedQueue((prev) => [
              ...prev.filter((_, index) => index !== currentRetryIndex),
              failedFact,
            ]);
          }
        }

        // Reset to allow another attempt after feedback
        setTimeout(() => {
          setHasAnswered(false);
        }, PRACTICE_TIMER_CONSTANTS.WRONG_ANSWER_RETRY_MS);
      }
    },
    [
      currentDigit,
      hasAnswered,
      isTTSPlaying,
      playChime,
      correctAnswerGiven,
      isRetryPhase,
      currentFact,
      failedQueue,
      currentRetryIndex,
      advanceToNext,
      onComplete,
    ]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.WE_DO}
        onBack={onBack}
        progress={completedCount / factsToPractice.length}
      />

      {/* Main Content Area - Centered Answer Choices */}
      <div className="flex-1 flex items-center justify-center px-6">
        <PracticeAnswerGrid
          choices={answerChoices}
          correctAnswer={currentDigit ?? 0}
          hasAnswered={hasAnswered}
          isTTSPlaying={isTTSPlaying}
          highlightCorrect={highlightCorrect}
          onChoiceClick={handleAnswerClick}
        />
      </div>
    </div>
  );
};

export default WeDoScreen;
